import React from "react";
import { ViewConfig } from "@lib/views";
import {
    CATALOG_ITEM_ACCOUNT_SOURCES,
    CatalogItemArchivedFilter,
    CatalogItemSort,
} from "@common/types/catalogItem";
import { InvoiceSource } from "@common/types/invoice";
import { View } from "@common/types/views/views";
import {
    CatalogItemColumn,
    CatalogItemView,
} from "@common/types/views/catalogItemViews";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { Button } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import { FiltersChangeFn, FilterTabs } from "../pageHeader/filters/lib";
import { CatalogItemFilters, CatalogItemRow } from "./catalogItems.types";
import { CatalogItemAccountFilter } from "./CatalogItemAccountFilter";

// TablePageHeader strips the Class tab on plans without classes. The
// Receivables account tab is gated like its column (accrual ledger access).
const BASE_CATALOG_ITEM_FILTERS = [
    FilterTabs.CLASS,
    FilterTabs.CATALOG_ITEM_INCOME_ACCOUNT,
];
const CATALOG_ITEM_FILTERS_WITH_RECEIVABLES = [
    ...BASE_CATALOG_ITEM_FILTERS,
    FilterTabs.CATALOG_ITEM_RECEIVABLES_ACCOUNT,
];

const ARCHIVED_FILTER_LABELS: Record<CatalogItemArchivedFilter, string> = {
    [CatalogItemArchivedFilter.ACTIVE]: "Active",
    [CatalogItemArchivedFilter.ALL]: "All",
    [CatalogItemArchivedFilter.ARCHIVED]: "Archived",
};

const ARCHIVED_FILTER_ITEMS: Array<PickerItemData<CatalogItemArchivedFilter>> =
    [
        CatalogItemArchivedFilter.ACTIVE,
        CatalogItemArchivedFilter.ALL,
        CatalogItemArchivedFilter.ARCHIVED,
    ].map((option) => ({
        value: option,
        label: ARCHIVED_FILTER_LABELS[option],
    }));

interface Props {
    search: string;
    setSearch: (search: string) => void;
    filters: CatalogItemFilters;
    showReceivablesAccountFilter: boolean;
    onFiltersChange: FiltersChangeFn;
    archivedFilter: CatalogItemArchivedFilter;
    onArchivedFilterChange: (filter: CatalogItemArchivedFilter) => void;
    sources: InvoiceSource[];
    onSourcesChange: (sources: InvoiceSource[]) => void;
    onAddItem: () => void;
    view: CatalogItemView;
    onViewChange: (view: View) => void;
    viewConfig: ViewConfig<CatalogItemRow, CatalogItemColumn, CatalogItemSort>;
    defaultView: CatalogItemView;
}

export const CatalogItemsListHeader: React.FC<Props> = ({
    search,
    setSearch,
    filters,
    showReceivablesAccountFilter,
    onFiltersChange,
    archivedFilter,
    onArchivedFilterChange,
    sources,
    onSourcesChange,
    onAddItem,
    view,
    onViewChange,
    viewConfig,
    defaultView,
}) => (
    <TablePageHeader
        filters={filters}
        onFiltersChange={onFiltersChange}
        search={search}
        onSearchChange={(value) => setSearch(value ?? "")}
        availableFilters={
            showReceivablesAccountFilter
                ? CATALOG_ITEM_FILTERS_WITH_RECEIVABLES
                : BASE_CATALOG_ITEM_FILTERS
        }
        hideClearButton
        isFullPage
    >
        <EntityFilterCombobox
            filters={filters}
            onChange={onFiltersChange}
            onlyBusiness
        />
        {CATALOG_ITEM_ACCOUNT_SOURCES.length > 1 && (
            <CatalogItemAccountFilter
                sources={sources}
                onChange={onSourcesChange}
            />
        )}
        <Picker.Select
            items={ARCHIVED_FILTER_ITEMS}
            value={archivedFilter}
            onValueChange={(value) => {
                if (value) {
                    onArchivedFilterChange(value);
                }
            }}
        />
        <ColumnSettings
            view={view}
            onViewChange={onViewChange}
            viewConfig={viewConfig}
            defaultView={defaultView}
        />
        <Button variant="primary" size="md" onClick={onAddItem}>
            Add item
        </Button>
    </TablePageHeader>
);
